import TopicTable from "../components/TopicTable";
import React, { useCallback, useEffect, useRef, useState } from "react";
import { BASE_URL_API_BLOG } from "../Utils/Constant";
import { getBasicAuthHeader } from "../Utils/auth";

import "../../assets/styles/components/_get-topic.scss";

const TOPICS_PER_PAGE = 25;
const NEWS_URL = "https://cms.iansnews.in/api/elastic/news/list/";

const topicDateFormatter = new Intl.DateTimeFormat("en-IN", {
  day: "2-digit", month: "short", year: "numeric", hour: "2-digit",
  minute: "2-digit", second: "2-digit", hour12: true, timeZoneName: "short",
});

function TopicTime({ value }) {
  const date = value ? new Date(value) : null;
  if (!date || Number.isNaN(date.getTime())) return <span>Not available</span>;
  return (
    <time dateTime={value}>
      {topicDateFormatter.format(date)}
    </time>
  );
}

function newestTopicFirst(first, second) {
  const firstDate = Date.parse(first.created_at);
  const secondDate = Date.parse(second.created_at);
  if (Number.isNaN(firstDate)) return Number.isNaN(secondDate) ? 0 : 1;
  if (Number.isNaN(secondDate)) return -1;
  return secondDate - firstDate;
}

function TopicFeed({ tag }) {
  const [topics, setTopics] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [hasMore, setHasMore] = useState(true);
  const page = useRef(1);
  const loadedTopics = useRef([]);
  const moreAvailable = useRef(true);
  const visibleCount = useRef(0);
  const pending = useRef(false);
  const controller = useRef(null);

  const loadMore = useCallback(async () => {
    if (pending.current) return;
    pending.current = true;
    const request = new AbortController();
    controller.current = request;
    setLoading(true);
    setError("");
    try {
      const targetCount = visibleCount.current + TOPICS_PER_PAGE;
      while (loadedTopics.current.length < targetCount && moreAvailable.current) {
        const params = new URLSearchParams({
          language: "english",
          page: String(page.current),
          page_size: String(TOPICS_PER_PAGE),
          website: "1",
        });
        if (tag) params.set("tags", tag);
        const response = await fetch(`${NEWS_URL}?${params}`, {
          signal: request.signal,
        });
        if (!response.ok)
          throw new Error(`Unable to load topics (${response.status}).`);
        const data = await response.json();
        if (!Array.isArray(data.results))
          throw new Error("The news service returned an unexpected response.");
        if (request.signal.aborted) return;
        const ids = new Set(loadedTopics.current.map((topic) => topic.id));
        const newTopics = data.results.filter((topic) => {
          if (ids.has(topic.id)) return false;
          ids.add(topic.id);
          return true;
        });
        loadedTopics.current = [
          ...loadedTopics.current,
          ...newTopics,
        ].sort(newestTopicFirst);
        moreAvailable.current =
          data.results.length > 0 &&
          (!Object.prototype.hasOwnProperty.call(data, "next") ||
            Boolean(data.next));
        page.current += 1;
      }
      visibleCount.current = targetCount;
      setTopics(loadedTopics.current.slice(0, targetCount));
      setHasMore(
        loadedTopics.current.length > targetCount || moreAvailable.current,
      );
    } catch (failure) {
      if (!request.signal.aborted)
        setError(failure.message || "Unable to load topics. Please try again.");
    } finally {
      if (controller.current === request) {
        pending.current = false;
        if (!request.signal.aborted) setLoading(false);
      }
    }
  }, [tag]);

  useEffect(() => {
    loadMore();
    return () => {
      if (controller.current) controller.current.abort();
      pending.current = false;
    };
  }, [loadMore]);


  return (
    <>
      <TopicTable topics={topics} Time={TopicTime} />
      <div className="topic-feed-footer">
        {loading && <p role="status">Loading topics…</p>}
        {error && (
          <div role="alert">
            <p>{error}</p>
            <button className="primary-link" type="button" onClick={loadMore}>
              Retry
            </button>
          </div>
        )}
        {!loading && !error && hasMore && (
          <button className="primary-link" type="button" onClick={loadMore}>
            Load more topics
          </button>
        )}
        {!loading && !error && !hasMore && (
          <p>
            {topics.length
              ? "You’ve reached the end of the topics."
              : "No topics found for this category."}
          </p>
        )}
      </div>
    </>
  );
}

export default function GetTopic() {
  const [categories, setCategories] = useState([]);
  const [tag, setTag] = useState("");
  const [categoryError, setCategoryError] = useState("");
  const [loadingCategories, setLoadingCategories] = useState(true);
  const [retry, setRetry] = useState(0);
  useEffect(() => {
    const controller = new AbortController();
    setLoadingCategories(true);
    setCategoryError("");
    async function loadCategories() {
      try {
        const response = await fetch(`${BASE_URL_API_BLOG}/GetBlogCategories`, {
          method: "POST",
          headers: {
            ...getBasicAuthHeader(),
            "Content-Type": "application/json",
          },
          body: JSON.stringify({ type: "0" }),
          signal: controller.signal,
        });
        if (!response.ok) throw new Error("Unable to load categories.");
        const data = await response.json();
        if (String(data.status) !== "0" || !Array.isArray(data.data))
          throw new Error(data.msg || "Unable to load categories.");
        if (!controller.signal.aborted) setCategories(data.data);
      } catch (error) {
        if (!controller.signal.aborted) setCategoryError(error.message);
      } finally {
        if (!controller.signal.aborted) setLoadingCategories(false);
      }
    }
    loadCategories();
    return () => controller.abort();
  }, [retry]);

  return (
    <section className="get-topic-page" aria-label="News topics">
      <div className="topic-toolbar">
        <header className="topic-page-header">
          <h1>Get Topic</h1>
        </header>
        <div className="topic-controls">
          <label htmlFor="topic-category">
            Category
            <select
              id="topic-category"
              value={tag}
              disabled={loadingCategories}
              onChange={(event) => setTag(event.target.value)}
            >
              <option value="">
                {loadingCategories ? "Loading categories…" : "All categories"}
              </option>
              {categories.map((category) => {
                const value =
                  category.slug ||
                  category.category_name
                    .trim()
                    .toLowerCase()
                    .replace(/\s+/g, "-");
                return (
                  <option key={value} value={value}>
                    {category.category_name}
                  </option>
                );
              })}
            </select>
          </label>
        </div>
      </div>
      {categoryError && (
        <div className="alert alert-warning" role="alert">
          {categoryError}{" "}
          <button type="button" onClick={() => setRetry((value) => value + 1)}>
            Retry categories
          </button>
        </div>
      )}
      <TopicFeed key={tag} tag={tag} />
    </section>
  );
}
